import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Eye, RefreshCw, Send } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SelectField } from "./common";
import { api, formatApiError } from "@/lib/api";
import { NOTIF_KINDS, NOTIF_STATUS, NOTIF_STYLE, fmtDateTime } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";

const History = ({ items }) => (
  <ul className="space-y-2" data-testid="notifications-history">
    {items.length === 0 && <li className="text-sm text-slate-400">Aucune notification pour cette intervention.</li>}
    {items.map((n) => (
      <li key={n.id} className="rounded-lg border border-cyan-400/10 bg-white/[0.03] p-3 text-sm" data-testid={`notification-${n.id}`}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="font-medium text-white">{NOTIF_KINDS[n.kind] || n.kind}{n.manual ? " (renvoi manuel)" : ""}{n.origin === "outlook" ? " · depuis Outlook" : ""}</span>
          <span className={NOTIF_STYLE[n.status] || "text-slate-300"}>{NOTIF_STATUS[n.status] || n.status}</span>
        </div>
        <p className="text-xs text-slate-400">
          {fmtDateTime(n.sent_at || n.created_at)}{n.to ? ` · ${n.to}` : ""}{n.error || n.reason ? ` · ${n.error || n.reason}` : ""}
        </p>
      </li>
    ))}
  </ul>
);

export const NotificationsDialog = ({ intervention, onOpenChange, onChanged }) => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [preview, setPreview] = useState(null);
  const [kind, setKind] = useState("confirmation");
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);
  const id = intervention?.id;

  const load = useCallback(() => {
    if (id) api.get(`/interventions/${id}/notifications`).then((r) => setItems(r.data));
  }, [id]);
  useEffect(() => {
    setPreview(null);
    setTo(user?.email || "");
    load();
  }, [load, user]);

  const run = async (fn, ok) => {
    setBusy(true);
    try {
      await fn();
      if (ok) toast.success(ok);
      load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  const showPreview = () => run(async () => setPreview((await api.post("/notifications/preview", { intervention_id: id, kind })).data));
  const sendTest = () => run(() => api.post("/notifications/test", { intervention_id: id, kind, to }), `E-mail de test transmis pour ${to}`);
  const resend = () => run(() => api.post(`/interventions/${id}/notifications/resend`), "Renvoi transmis au serveur d'envoi");

  return (
    <Dialog open={!!intervention} onOpenChange={onOpenChange}>
      <DialogContent data-testid="notifications-dialog" className="max-h-[92vh] overflow-y-auto bg-[#0F1F3D] border-cyan-400/20 sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">E-mails client · {intervention?.title}</DialogTitle>
          <DialogDescription className="text-slate-400">
            {intervention?.notify_client === false ? "Notification désactivée pour cette intervention." : "« Transmis » signifie accepté par le serveur d'envoi ; la réception n'est pas garantie."}
          </DialogDescription>
        </DialogHeader>
        <History items={items} />
        <div className="flex flex-wrap items-end gap-2 border-t border-cyan-400/10 pt-4">
          <SelectField className="w-44" value={kind} onChange={setKind} options={NOTIF_KINDS} testid="notification-kind-select" />
          <Button variant="secondary" onClick={showPreview} disabled={busy} data-testid="notification-preview-button"><Eye className="mr-1 h-4 w-4" />Prévisualiser</Button>
          <Button variant="secondary" onClick={resend} disabled={busy} data-testid="notification-resend-button"><RefreshCw className="mr-1 h-4 w-4" />Renvoyer au client</Button>
        </div>
        {user?.role === "admin" && (
          <div className="flex flex-wrap items-center gap-2">
            <Input className="max-w-xs" value={to} onChange={(e) => setTo(e.target.value)} placeholder="adresse@test.fr" data-testid="notification-test-email-input" />
            <Button onClick={sendTest} disabled={busy || !to} className="dt-glow" data-testid="notification-test-button"><Send className="mr-1 h-4 w-4" />Envoi de test</Button>
          </div>
        )}
        {preview && (
          <div className="space-y-2" data-testid="notification-preview">
            {preview.error ? <p className="text-sm text-amber-300">{preview.error}</p> : (
              <>
                <p className="text-sm text-slate-300">Objet : <span className="text-white">{preview.subject}</span></p>
                <p className="text-xs text-slate-400">Destinataire : {preview.to || "aucune adresse e-mail"} · {preview.ics ? (preview.attachments_supported ? "fichier .ics joint" : ".ics non joint dans cet environnement (SMTP requis)") : "sans fichier .ics"}</p>
                <iframe title="Aperçu e-mail" srcDoc={preview.html} sandbox="" className="h-[520px] w-full rounded-lg bg-white" data-testid="notification-preview-frame" />
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
